import React, { useState, useEffect, useCallback } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { Helmet } from "react-helmet";
import SignInPage from "./pages/SignInPage";
import HomePage from "./pages/HomePage";
import ResultsPage from "./pages/ResultsPage";
import DetailsPage from "./pages/DetailsPage";
import DevisPage from "./pages/DevisPage";
import LoadingPage from "./pages/LoadingPage";
import RechargePage from "./pages/RechargePage";
import TransactionHistoryPage from "./pages/TransactionHistoryPage";
import Header from "./components/Header";
import BookingsModal from "./components/BookingsModal";
import * as hotelApi from "./services/hotelApi";
import PartnersManagementPage from "./pages/PartnersManagementPage";
import RechargeRequestsPage from "./pages/RechargeRequestsPage";
import MargeB2BControlPage from "./pages/MargeB2BControlPage";
import Swal from "sweetalert2";
import { sessionManager } from './utils/sessionManager';


// Protected Route wrapper
function ProtectedRoute({ children, isLoggedIn }) {
  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

// Main App Content
function AppContent() {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  // const [searchData, setSearchData] = useState(null);
  const [searchData, setSearchData] = useState(() => {
  const saved = localStorage.getItem('searchData');
  if (saved) {
    try {
      return JSON.parse(saved);
    } catch (e) {
      return null;
    }
  }
  return null;
});

const [showSessionWarning, setShowSessionWarning] = useState(false);


  const [searchResults, setSearchResults] = useState(() => {
  const saved = localStorage.getItem('searchResults');
  if (saved) {
    try {
      return JSON.parse(saved);
    } catch (e) {
      return [];
    }
  }
  return [];
});


  // const [selectedHotel, setSelectedHotel] = useState(null);
  const [selectedHotel, setSelectedHotel] = useState(() => {
  const saved = localStorage.getItem('selectedHotel');
  if (saved) {
    try {
      return JSON.parse(saved);
    } catch (e) {
      return null;
    }
  }
  return null;
});

  const [isLoading, setIsLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [showBookingsModal, setShowBookingsModal] = useState(false);
  const [isLoadingBookings, setIsLoadingBookings] = useState(false);
  // const [searchResults, setSearchResults] = useState([]);


  // const [selectedPension, setSelectedPension] = useState(null);
  const [selectedPension, setSelectedPension] = useState(() => {
  const saved = localStorage.getItem('bookingData');
  if (saved) {
    try {
      const data = JSON.parse(saved);
      return data.pension || null;
    } catch (e) {
      return null;
    }
  }
  return null;
});

  // const [selectedRoomVariants, setSelectedRoomVariants] = useState(null);
  const [selectedRoomVariants, setSelectedRoomVariants] = useState(() => {
  const saved = localStorage.getItem('bookingData');
  if (saved) {
    try {
      const data = JSON.parse(saved);
      return data.roomVariants || null;
    } catch (e) {
      return null;
    }
  }
  return null;
});

  // const [selectedAllocation, setSelectedAllocation] = useState(null);

  const [selectedAllocation, setSelectedAllocation] = useState(() => {
  const saved = localStorage.getItem('bookingData');
  if (saved) {
    try {
      const data = JSON.parse(saved);
      return data.allocation || null;
    } catch (e) {
      return null;
    }
  }
  return null;
});

  // User data
  const [userData, setUserData] = useState({
    id: null,
    libelle: "",
    nom: "",
    prenom: "",
    email: "",
    balance: 0,
    margin: 0,
    margeB2B: 0,
  });

  const [isAdmin, setIsAdmin] = useState(false);

  // ✅ Use sessionStorage to persist static data across navigation
  const [staticData, setStaticData] = useState(() => {
    const cached = sessionStorage.getItem("staticData");
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch (e) {
        return {
          cities: [],
          boardings: [],
          hotels: [],
          tags: [],
          currencies: [],
          isLoaded: false,
        };
      }
    }
    return {
      cities: [],
      boardings: [],
      hotels: [],
      tags: [],
      currencies: [],
      isLoaded: false,
    };
  });

  // Check login on mount
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const storedUser = localStorage.getItem('userData');
    
    if (token && storedUser) {
      setIsLoggedIn(true);
      const user = JSON.parse(storedUser);
      setUserData(user);
      setIsAdmin(user.id_partenaire === 1);
    }
    
    // ✅ Mark auth check as complete
    setIsCheckingAuth(false);
  }, []);

const handleSessionWarning = useCallback(() => {
  setShowSessionWarning(true);
  
  Swal.fire({
    icon: 'warning',
    title: 'Session expirée bientôt',
    text: 'Votre session expirera dans 5 minutes. Continuez à utiliser l\'application pour rester connecté.',
    confirmButtonText: 'Continuer',
    confirmButtonColor: '#FF7B47',
    timer: 10000,
    timerProgressBar: true
  }).then(() => {
    setShowSessionWarning(false);
  });
}, []);

const handleSessionTimeout = useCallback(() => {
  // ✅ FORCE LOGOUT IMMEDIATELY
  sessionManager.stopTracking();
  
  // Clear everything
  localStorage.removeItem("authToken");
  localStorage.removeItem("userData");
  sessionStorage.removeItem("staticData");
  sessionStorage.removeItem("searchData");
  localStorage.removeItem("searchData");
  localStorage.removeItem("searchResults");
  localStorage.removeItem("selectedHotel");
  localStorage.removeItem("bookingData");
  
  setIsLoggedIn(false);
  setSearchData(null);
  setSelectedHotel(null);
  
  // ✅ Show popup AFTER logout
  Swal.fire({
    icon: 'info',
    title: 'Session expirée',
    text: 'Vous avez été déconnecté en raison d\'inactivité (1 heure).',
    confirmButtonText: 'Reconnexion',
    confirmButtonColor: '#FF7B47',
    allowOutsideClick: false
  }).then(() => {
    navigate('/login');
  });
}, [navigate]);

// ✅ Start tracking with proper dependencies
useEffect(() => {
  if (isLoggedIn) {
    sessionManager.startTracking(handleSessionWarning, handleSessionTimeout);
  } else {
    sessionManager.stopTracking();
  }
  
  return () => {
    sessionManager.stopTracking();
  };
}, [isLoggedIn, handleSessionWarning, handleSessionTimeout]);

  useEffect(() => {
  if (!isLoggedIn) return;

  const refreshBalance = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await fetch('https://djebooking.com/api/auth/me', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) return;

      const freshUser = await response.json();

      setUserData(prev => {
        const updated = { ...prev, balance: freshUser.balance };
        localStorage.setItem('userData', JSON.stringify(updated));
        return updated;
      });
    } catch (err) {
      // Silent fail — don't disrupt UX if this background fetch fails
    }
  };

  // Refresh on tab focus
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refreshBalance();
  });

  // Also refresh every 60 seconds while the tab is open
  const interval = setInterval(refreshBalance, 60000);

  return () => {
    window.removeEventListener('visibilitychange', refreshBalance);
    clearInterval(interval);
  };
}, [isLoggedIn]);

  // Load static data ONCE when logged in
useEffect(() => {
  if (isLoggedIn && !staticData.isLoaded) {
    loadStaticData();
  }
}, [isLoggedIn, staticData.isLoaded]);


  async function loadStaticData() {
    try {

      const [cities, boardings, hotels, tags, currencies] = await Promise.all([
        hotelApi.fetchCities(),
        hotelApi.fetchBoardings(),
        hotelApi.fetchHotels(),
        hotelApi.fetchTags(),
        hotelApi.fetchCurrencies(),
      ]);

      const newStaticData = {
        cities,
        boardings,
        hotels,
        tags,
        currencies,
        isLoaded: true,
      };


      setStaticData(newStaticData);
      sessionStorage.setItem("staticData", JSON.stringify(newStaticData));
    } catch (error) {

      Swal.fire({
        icon: "error",
        title: "Erreur de connexion API",
        text: "Impossible de charger les données.",
        confirmButtonText: "OK",
      });
    }
  }

  const handleLogin = async (credentials) => {
    try {

      const response = await fetch("https://djebooking.com/api/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          login: credentials.login,
          password: credentials.password,
          captchaToken: credentials.captchaToken,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Login failed");
      }

      const data = await response.json();

      localStorage.setItem("authToken", data.token);
      localStorage.setItem("userData", JSON.stringify(data.user));

      setUserData(data.user);
      setIsLoggedIn(true);

      setIsAdmin(data.user.id_partenaire === 1 || data.user.id === 1);

      navigate("/");
    } catch (error) {

      Swal.fire({
        icon: "error",
        title: "Erreur de connexion",
        text: error.message || "Identifiants invalides",
        confirmButtonColor: "#FF7B47",
      });

      throw error;
    }
  };

  const handleLogout = () => {
    sessionManager.stopTracking();
    localStorage.removeItem("authToken");
    localStorage.removeItem("userData");
    sessionStorage.removeItem("staticData");
    sessionStorage.removeItem("searchData");
    setIsLoggedIn(false);
    setSearchData(null);
    setSelectedHotel(null);
    navigate("/login");
  };

  const handleMarginChange = (newMargin) => {

    setUserData((prevUserData) => ({
      ...prevUserData,
      margin: newMargin,
    }));

    recalculatePricesWithNewMargin(newMargin);
  };

  const handleDetailsSearch = async (data) => {
    try {
      if (selectedHotel) {
        // This ensures we only set the flag when updating an existing hotel
        // Not when initially loading
        localStorage.setItem("userTriggeredUpdate", "true");
      }


      const checkIn = hotelApi.formatDateForAPI(data.checkIn);
      const checkOut = hotelApi.formatDateForAPI(data.checkOut);

      const searchParams = {
        cityId: data.destination,
        hotelId: data.hotelId, // ✅ ADD THIS - Only search THIS hotel!
        checkIn: checkIn,
        checkOut: checkOut,
        rooms: data.rooms.map((room) => ({
          adults: room.adults,
          children: room.children,
        })),
        onlyAvailable: true,
        currency: "TND",
      };


      // ✅ This should now return ONLY the specified hotel
      const results = await hotelApi.searchHotels(searchParams);

      if (!results || !Array.isArray(results)) {
        throw new Error("Aucun résultat trouvé");
      }

      // ✅ Should be the first (and only) result
      const updatedHotel =
        results.find((r) => r.Hotel.Id === data.hotelId) || results[0];

      if (!updatedHotel) {
        throw new Error(
          "Cet hôtel n'est pas disponible pour ces dates/voyageurs",
        );
      }

      // Rest of your transformation code stays exactly the same...
      const pensions = [];

      if (
        updatedHotel.Price?.Boarding &&
        Array.isArray(updatedHotel.Price.Boarding)
      ) {
        updatedHotel.Price.Boarding.forEach((boarding) => {
          const roomsBySlot = {};
          const fallbackSuggestions = {};

          if (boarding.Pax && Array.isArray(boarding.Pax)) {
            const usedRoomSlots = new Set();

            boarding.Pax.forEach((pax) => {
              const paxAdults = pax.Adult || 0;
              const paxChildren = pax.Child?.length || 0;

              const matchingRoomIndex = data.rooms.findIndex(
                (requestedRoom, idx) => {
                  if (usedRoomSlots.has(idx)) return false;

                  const reqAdults = requestedRoom.adults;
                  const reqChildren = requestedRoom.children?.length || 0;
                  return reqAdults === paxAdults && reqChildren === paxChildren;
                },
              );

              if (matchingRoomIndex === -1) return;

              usedRoomSlots.add(matchingRoomIndex);

              if (pax.Rooms && Array.isArray(pax.Rooms)) {
                pax.Rooms.forEach((room) => {
                  const roomPrice = parseFloat(room.Price);

                  if (!isNaN(roomPrice)) {
                    const totalMarginPercent =
                      (userData.margeB2B + userData.margin) / 100;
                    const priceWithBothMargins =
                      roomPrice * (1 + totalMarginPercent);
                    const gomaxCommission =
                      roomPrice * (userData.margeB2B / 100);
                    const partnerProfit = roomPrice * (userData.margin / 100);
                    const purchasePrice = roomPrice + gomaxCommission;

                    const roomData = {
                      id: room.Id,
                      name: room.Name,
                      price: priceWithBothMargins,
                      purchasePrice: purchasePrice,
                      originalPrice: roomPrice,
                      gomaxCommission: gomaxCommission,
                      partnerProfit: partnerProfit,
                      onRequest: room.OnRequest || false,
                      notRefundable: room.NotRefundable || false,
                      cancellationPolicy: room.CancellationPolicy || [],
                      view: room.View || [],
                      supplement: room.Supplement || [],
                      roomSlotIndex: matchingRoomIndex,
                      adults: paxAdults,
                      children: paxChildren,
                      quantity: room.Quantity || 0 
                    };

                    if (!roomsBySlot[matchingRoomIndex]) {
                      roomsBySlot[matchingRoomIndex] = [];
                    }
                    roomsBySlot[matchingRoomIndex].push(roomData);
                  }
                });
              }
            });

            // ✅ Fallback logic
            data.rooms.forEach((requestedRoom, roomIndex) => {
              if (roomsBySlot[roomIndex]) return;

              const totalPeople =
                requestedRoom.adults + (requestedRoom.children?.length || 0);

              const combination = findBestRoomCombination(
                totalPeople,
                requestedRoom.adults,
                requestedRoom.children?.length || 0,
                boarding.Pax,
                userData,
              );

              if (combination) {
                fallbackSuggestions[roomIndex] = combination;
              }
            });
          }

          const hasExactRooms = Object.keys(roomsBySlot).length > 0;
          const hasFallbacks = Object.keys(fallbackSuggestions).length > 0;

          if (hasExactRooms || hasFallbacks) {
            const allRooms = Object.values(roomsBySlot).flat();

            let minPrice = 0;
if (Object.keys(roomsBySlot).length > 0) {
  // For each slot, pick its cheapest room, then sum them all
  minPrice = Object.values(roomsBySlot).reduce((sum, slotRooms) => {
    const cheapestInSlot = Math.min(...slotRooms.map(r => r.price));
    return sum + cheapestInSlot;
  }, 0);
} else if (hasFallbacks) {
  // For fallbacks, sum the cheapest room from each fallback group
  minPrice = Object.values(fallbackSuggestions).reduce((sum, fallback) => {
    if (!fallback?.rooms) return sum;
    const fallbackGroupMin = fallback.rooms.reduce((groupSum, roomGroup) => {
      const cheapestInGroup = Math.min(...roomGroup.availableRooms.map(r => r.price));
      return groupSum + cheapestInGroup;
    }, 0);
    return sum + fallbackGroupMin;
  }, 0);
}

            pensions.push({
              type:
                boarding.Code?.toLowerCase().replace(/\s+/g, "-") || "unknown",
              label: boarding.Name,
              boardingId: boarding.Id,
              code: boarding.Code,
              rooms: allRooms,
              roomsBySlot: roomsBySlot,
              fallbackSuggestions: fallbackSuggestions,
              minPrice: minPrice,
            });
          }
        });
      }

      const transformedHotel = {
        id: updatedHotel.Hotel.Id,
        name: updatedHotel.Hotel.Name,
        stars: parseInt(updatedHotel.Hotel.Category?.Star) || 3,
        starCategory: updatedHotel.Hotel.Category?.Title || "",
        location: updatedHotel.Hotel.City?.Name || "",
        image:
          updatedHotel.Hotel.Image ||
          "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80",
        ratingText: "Disponible",
        available: updatedHotel.Available,
        facilities: updatedHotel.Hotel.Facilities?.map((f) => f.Title) || [],
        pensions: pensions,
        price: pensions.length > 0 ? pensions[0].minPrice : 0,
        rawData: updatedHotel,
      };

      const formattedSearchData = {
        destination: searchData.destination,
        destinationId: data.destination,
        checkIn: data.checkIn.toLocaleDateString("fr-FR"),
        checkOut: data.checkOut.toLocaleDateString("fr-FR"),
        rooms: data.rooms,
      };

      // ✅ Update state directly (NO navigation!)
      setSearchData(formattedSearchData);
      setSelectedHotel(transformedHotel);

      // ✅ Save to localStorage
      localStorage.setItem("selectedHotel", JSON.stringify(transformedHotel));
      localStorage.setItem("searchData", JSON.stringify(formattedSearchData));

    } catch (error) {

      Swal.fire({
        icon: "error",
        title: "Erreur",
        text: error.message,
        confirmButtonText: "OK",
      });
    }
  };

  const handlePartnersManagement = () => {

    navigate("/admin/partners");
  };

  const handleRechargeRequests = () => {

    navigate("/admin/recharge-requests");
  };

  const handleMargeB2BControl = () => {

    navigate("/admin/marge-b2b");
  };

const handleSearch = async (data, recalculateOnly = false) => {
  if (recalculateOnly) {
    recalculatePricesWithNewMargin();
    return;
  }

  localStorage.removeItem('selectedHotel');
  localStorage.removeItem('bookingData');

  // ✅ START TIMING
  const perfStart = performance.now();

  try {
    let cityName = data.destination;
    let cityId = data.destination;

    if (typeof data.destination === 'number' || !isNaN(parseInt(data.destination))) {
      const city = staticData.cities.find(c => c.Id === parseInt(data.destination));
      if (city) {
        cityName = city.Name;
        cityId = city.Id;
      }
    } else {
      const city = staticData.cities.find(c => 
        c.Name.toLowerCase() === data.destination.toLowerCase()
      );
      if (city) {
        cityName = city.Name;
        cityId = city.Id;
      } else {
        throw new Error(`Ville non trouvée: ${data.destination}`);
      }
    }

    const formattedSearchData = {
      destination: cityName,
      destinationId: cityId,
      checkIn: data.checkIn.toLocaleDateString('fr-FR'),
      checkOut: data.checkOut.toLocaleDateString('fr-FR'),
      rooms: data.rooms
    };
    
    const beforeStateUpdate = performance.now();
    setSearchData(formattedSearchData);
    setIsLoading(true);
    
    navigate('/loading');

    const checkIn = hotelApi.formatDateForAPI(data.checkIn);
    const checkOut = hotelApi.formatDateForAPI(data.checkOut);

    const searchParams = {
      cityId: cityId,
      checkIn: checkIn,
      checkOut: checkOut,
      rooms: data.rooms.map(room => ({
        adults: room.adults,
        children: room.children
      })),
      onlyAvailable: true,
      currency: 'TND'
    };

    // ✅ MEASURE API CALL
    const apiStart = performance.now();
    
    const results = await hotelApi.searchHotels(searchParams);
    
    const apiEnd = performance.now();
    const apiDuration = ((apiEnd - apiStart) / 1000).toFixed(2);

    if (!results || !Array.isArray(results)) {
      throw new Error('Aucun hôtel trouvé pour cette recherche');
    }

    // ✅ MEASURE TRANSFORMATION
    const transformStart = performance.now();

    const transformedHotels = results.map(result => {
      const pensions = [];
      
      if (result.Price?.Boarding && Array.isArray(result.Price.Boarding)) {
        result.Price.Boarding.forEach(boarding => {
          const roomsBySlot = {};
          const fallbackSuggestions = {};
          
          if (boarding.Pax && Array.isArray(boarding.Pax)) {
            const usedRoomSlots = new Set();
            
            boarding.Pax.forEach(pax => {
              const paxAdults = pax.Adult || 0;
              const paxChildren = pax.Child?.length || 0;
              
              const matchingRoomIndex = data.rooms.findIndex((requestedRoom, idx) => {
                if (usedRoomSlots.has(idx)) return false;
                
                const reqAdults = requestedRoom.adults;
                const reqChildren = requestedRoom.children?.length || 0;
                return reqAdults === paxAdults && reqChildren === paxChildren;
              });
              
              if (matchingRoomIndex === -1) return;
              
              usedRoomSlots.add(matchingRoomIndex);
                
              if (pax.Rooms && Array.isArray(pax.Rooms)) {
                pax.Rooms.forEach(room => {
                  const roomPrice = parseFloat(room.Price);
                  
                  if (!isNaN(roomPrice)) {
                    const totalMarginPercent = (userData.margeB2B + userData.margin) / 100;
                    const priceWithBothMargins = roomPrice * (1 + totalMarginPercent);
                    const gomaxCommission = roomPrice * (userData.margeB2B / 100);
                    const partnerProfit = roomPrice * (userData.margin / 100);
                    const purchasePrice = roomPrice + gomaxCommission;

                    const roomData = {
                      id: room.Id,
                      name: room.Name,
                      price: priceWithBothMargins,
                      purchasePrice: purchasePrice,
                      originalPrice: roomPrice,
                      gomaxCommission: gomaxCommission,
                      partnerProfit: partnerProfit,
                      onRequest: room.OnRequest || false,
                      notRefundable: room.NotRefundable || false,
                      cancellationPolicy: room.CancellationPolicy || [],
                      view: room.View || [],
                      supplement: room.Supplement || [],
                      roomSlotIndex: matchingRoomIndex,
                      adults: paxAdults,
                      children: paxChildren,
                      quantity: room.Quantity || 0 
                    };

                    if (!roomsBySlot[matchingRoomIndex]) {
                      roomsBySlot[matchingRoomIndex] = [];
                    }
                    roomsBySlot[matchingRoomIndex].push(roomData);
                  }
                });
              }
            });

            data.rooms.forEach((requestedRoom, roomIndex) => {
              if (roomsBySlot[roomIndex]) return;
              
              const totalPeople = requestedRoom.adults + (requestedRoom.children?.length || 0);
              
              const combination = findBestRoomCombination(
                totalPeople, 
                requestedRoom.adults, 
                requestedRoom.children?.length || 0,
                boarding.Pax,
                userData
              );
              
              if (combination) {
                fallbackSuggestions[roomIndex] = combination;
              }
            });
          }

          const hasExactRooms = Object.keys(roomsBySlot).length > 0;
          const hasFallbacks = Object.keys(fallbackSuggestions).length > 0;
          
          if (hasExactRooms || hasFallbacks) {
            const allRooms = Object.values(roomsBySlot).flat();

            let minPrice = 0;
if (Object.keys(roomsBySlot).length > 0) {
  // For each slot, pick its cheapest room, then sum them all
  minPrice = Object.values(roomsBySlot).reduce((sum, slotRooms) => {
    const cheapestInSlot = Math.min(...slotRooms.map(r => r.price));
    return sum + cheapestInSlot;
  }, 0);
} else if (hasFallbacks) {
  // For fallbacks, sum the cheapest room from each fallback group
  minPrice = Object.values(fallbackSuggestions).reduce((sum, fallback) => {
    if (!fallback?.rooms) return sum;
    const fallbackGroupMin = fallback.rooms.reduce((groupSum, roomGroup) => {
      const cheapestInGroup = Math.min(...roomGroup.availableRooms.map(r => r.price));
      return groupSum + cheapestInGroup;
    }, 0);
    return sum + fallbackGroupMin;
  }, 0);
}
            
            pensions.push({
              type: boarding.Code?.toLowerCase().replace(/\s+/g, '-') || 'unknown',
              label: boarding.Name,
              boardingId: boarding.Id,
              code: boarding.Code,
              rooms: allRooms,
              roomsBySlot: roomsBySlot,
              fallbackSuggestions: fallbackSuggestions,
              minPrice: minPrice
            });
          }
        });
      }

      return {
        id: result.Hotel.Id,
        name: result.Hotel.Name,
        stars: parseInt(result.Hotel.Category?.Star) || 3,
        starCategory: result.Hotel.Category?.Title || '',
        location: result.Hotel.City?.Name || '',
        image: result.Hotel.Image || 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80',
        ratingText: 'Disponible',
        available: result.Available,
        facilities: result.Hotel.Facilities?.map(f => f.Title) || [],
        pensions: pensions,
        price: pensions.length > 0 ? pensions[0].minPrice : 0,
        rawData: result
      };
    });

    const transformEnd = performance.now();
    const transformDuration = ((transformEnd - transformStart) / 1000).toFixed(2);

    const hotelsWithAvailability = transformedHotels.map(hotel => {
      const hasExactMatch = hotel.pensions.some(pension => 
        Object.keys(pension.roomsBySlot || {}).length > 0
      );
      
      const hasFallback = hotel.pensions.some(pension =>
        Object.keys(pension.fallbackSuggestions || {}).length > 0
      );
      
      return {
        ...hotel,
        hasExactMatch,
        hasFallback,
        availabilityType: hasExactMatch ? 'exact' : hasFallback ? 'fallback' : 'none'
      };
    });

    const availableHotels = hotelsWithAvailability.filter(h => 
      h.availabilityType !== 'none' && h.pensions.length > 0
    );


    localStorage.setItem('searchData', JSON.stringify(formattedSearchData));
    localStorage.setItem('searchResults', JSON.stringify(availableHotels));

    const beforeFinalStateUpdate = performance.now();
    setSearchResults(availableHotels);
    setIsLoading(false);
    
    navigate('/results');

    // ✅ TOTAL TIME
    const perfEnd = performance.now();
    const totalDuration = ((perfEnd - perfStart) / 1000).toFixed(2);
    

  } catch (error) {
    const perfEnd = performance.now();
    const totalDuration = ((perfEnd - perfStart) / 1000).toFixed(2);
    
    setIsLoading(false);
    
    Swal.fire({
      icon: 'error',
      title: 'Erreur de recherche',
      text: error.message,
      confirmButtonText: 'Réessayer'
    }).then(() => {
      navigate('/');
    });
  }
};

  function findBestRoomCombination(
    totalPeople,
    adults,
    children,
    availablePax,
    userData,
  ) {


    const possibleSplits = [];

    for (let room1Size = 1; room1Size <= totalPeople - 1; room1Size++) {
      const room2Size = totalPeople - room1Size;

      if (
        room1Size >= 1 &&
        room1Size <= 5 &&
        room2Size >= 1 &&
        room2Size <= 5
      ) {
        possibleSplits.push({
          config1: { adults: room1Size, children: 0 },
          config2: { adults: room2Size, children: 0 },
        });
      }
    }



    for (const split of possibleSplits) {
      const pax1 = availablePax.find(
        (p) =>
          p.Adult === split.config1.adults &&
          (p.Child?.length || 0) === split.config1.children,
      );
      const pax2 = availablePax.find(
        (p) =>
          p.Adult === split.config2.adults &&
          (p.Child?.length || 0) === split.config2.children,
      );


      if (
        pax1 &&
        pax1.Rooms &&
        pax1.Rooms.length > 0 &&
        pax2 &&
        pax2.Rooms &&
        pax2.Rooms.length > 0
      ) {
        const rooms1 = processPaxRooms(
          pax1.Rooms,
          userData,
          split.config1.adults,
          split.config1.children,
        );
        const rooms2 = processPaxRooms(
          pax2.Rooms,
          userData,
          split.config2.adults,
          split.config2.children,
        );

        if (rooms1.length > 0 && rooms2.length > 0) {
          return {
            type: "split",
            description: `${split.config1.adults} adulte(s) + ${split.config2.adults} adulte(s) dans 2 chambres séparées`,
            rooms: [
              { config: split.config1, availableRooms: rooms1 },
              { config: split.config2, availableRooms: rooms2 },
            ],
            totalRooms: 2,
          };
        }
      }
    }

    return null;
  }

  function processPaxRooms(paxRooms, userData, adults, children) {
    return paxRooms
      .map((room) => {
        const roomPrice = parseFloat(room.Price);
        if (isNaN(roomPrice)) return null;

        const totalMarginPercent = (userData.margeB2B + userData.margin) / 100;
        const priceWithBothMargins = roomPrice * (1 + totalMarginPercent);
        const gomaxCommission = roomPrice * (userData.margeB2B / 100);
        const partnerProfit = roomPrice * (userData.margin / 100);
        const purchasePrice = roomPrice + gomaxCommission;

        return {
          id: room.Id,
          name: room.Name,
          price: priceWithBothMargins,
          purchasePrice: purchasePrice,
          originalPrice: roomPrice,
          gomaxCommission: gomaxCommission,
          partnerProfit: partnerProfit,
          onRequest: room.OnRequest || false,
          notRefundable: room.NotRefundable || false,
          cancellationPolicy: room.CancellationPolicy || [],
          view: room.View || [],
          supplement: room.Supplement || [],
          adults: adults,
          children: children,
        };
      })
      .filter((r) => r !== null);
  }

  const recalculatePricesWithNewMargin = (newMargin) => {
    if (!searchResults || searchResults.length === 0) return;

    const marginToUse = newMargin !== undefined ? newMargin : userData.margin;


    const updatedHotels = searchResults.map((hotel) => {
      const updatedPensions = hotel.pensions.map((pension) => {
        const updatedRooms = pension.rooms.map((room) => {
          const originalPrice = room.originalPrice;

          const totalMarginPercent = (userData.margeB2B + marginToUse) / 100;
          const priceWithBothMargins = originalPrice * (1 + totalMarginPercent);
          const gomaxCommission = originalPrice * (userData.margeB2B / 100);
          const partnerProfit = originalPrice * (marginToUse / 100);
          const purchasePrice = originalPrice + gomaxCommission;

          return {
            ...room,
            price: priceWithBothMargins,
            purchasePrice: purchasePrice,
            gomaxCommission: gomaxCommission,
            partnerProfit: partnerProfit,
          };
        });

        return {
          ...pension,
          rooms: updatedRooms,
          minPrice: Object.keys(pension.roomsBySlot || {}).length > 0
  ? Object.values(pension.roomsBySlot).reduce((sum, slotRooms) => {
      const cheapest = Math.min(...slotRooms.map(r => r.price));
      return sum + cheapest;
    }, 0)
  : 0,

        };
      });

      return {
        ...hotel,
        pensions: updatedPensions,
        price: updatedPensions.length > 0 ? updatedPensions[0].minPrice : 0,
      };
    });

    setSearchResults(updatedHotels);
  };

  const handleSelectHotel = (hotel) => {
    localStorage.setItem("selectedHotel", JSON.stringify(hotel));
    localStorage.setItem("searchData", JSON.stringify(searchData));

    setSelectedHotel(hotel);
    navigate("/details");
  };

const fetchBookings = async () => {
  try {
    setIsLoadingBookings(true);
    const token = localStorage.getItem("authToken");

    const url = isAdmin
      ? "https://djebooking.com/api/transactions/admin/all"
      : "https://djebooking.com/api/reservations";

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("Erreur lors du chargement des réservations");
    }

    const data = await response.json();

const transformedBookings = data.reservations.map((resa) => ({
  id: resa.id_resa,
  refCommande: resa.ref_commande,
  mygoBookingId: resa.mygo_booking_id || null,
  hotelName: resa.nom_hotel || `Hôtel #${resa.id_hotel}`,
  location: 'Tunisie',
  checkIn: new Date(resa.date_arr).toLocaleDateString('fr-FR'),
  checkOut: new Date(resa.date_dep).toLocaleDateString('fr-FR'),
  bookingDate: new Date(resa.date_resa).toLocaleDateString('fr-FR'),
  guests: `${resa.nb_adl} adulte(s)${resa.nb_enf > 0 ? `, ${resa.nb_enf} enfant(s)` : ''}`,
  totalPrice: parseFloat(resa.montant_vente),
  commission: parseFloat(resa.commission),
  purchasePrice: parseFloat(resa.montant_achat),
  clientName: `${resa.client_prenom || ''} ${resa.client_nom || ''}`.trim(),
  clientEmail: resa.client_mail,
  clientPhone: resa.client_tel,
  partnerName: isAdmin ? resa.libelle_partenaire || '' : '',
  partnerFullName: isAdmin
    ? `${resa.prenom_partenaire || ''} ${resa.nom_partenaire || ''}`.trim()
    : '',
  status:
  resa.etat_commande === 'Valider'          ? 'confirmed'    :
  resa.etat_commande === 'En Attente'       ? 'sur_demande'  :
  resa.etat_commande === 'MyGO_En_Attente'  ? 'mygo_pending' :
  resa.etat_commande === 'Annuler'          ? 'cancelled'    :
  resa.etat_commande === 'Echec'            ? 'error'        : 'pending',
  paymentStatus: resa.etat_paiement,
  rooms: resa.nb_room,
  details: resa.details || [],
  mygoVoucherUrl: resa.mygo_voucher_url || null,
  mygoVoucherModified: resa.mygo_voucher_modified  || null,
  isB2B: resa.is_b2b === 1 || resa.is_b2b === true,
}));
transformedBookings.sort((a, b) => b.id - a.id);

    setBookings(transformedBookings);
  } catch (error) {
    Swal.fire({
      icon: "error",
      title: "Erreur",
      text: "Impossible de charger les réservations",
      confirmButtonColor: "#FF7B47",
    });
  } finally {
    setIsLoadingBookings(false);
  }
};

  const handleBooking = (hotel, pension, roomVariants, roomSelectionData) => {


    setSelectedPension(pension);
    setSelectedRoomVariants(roomVariants);

    // ✅ Create allocation array from roomSelectionData
    const allocation = roomSelectionData.map((slot) => ({
      slotIndex: slot.slotIndex,
      requestedAdults: slot.requestedAdults || slot.adults || 0,
      requestedChildren: slot.requestedChildren || slot.children || 0,
      totalPeople: slot.totalPeople,
    }));

    setSelectedAllocation(allocation);

    const bookingData = {
      hotel,
      pension,
      roomVariants,
      roomSelectionData,
      allocation,
      searchData,
    };
    localStorage.setItem("bookingData", JSON.stringify(bookingData));

    navigate("/devis");
  };

  const handleRechargeClick = () => {
    navigate("/recharge");
  };

  const handleBalanceUpdate = (newBalance) => {
    const updatedUser = {
      ...userData,
      balance: newBalance,
    };
    setUserData(updatedUser);
    localStorage.setItem("userData", JSON.stringify(updatedUser));
  };

  const handleBookingsClick = () => {
    fetchBookings();
    setShowBookingsModal(true);
  };

  const handleGoHome = () => {
    navigate("/");
  };

  // ✅ NEW: Transaction History handler
  const handleTransactionHistoryClick = () => {
    navigate("/transactions");
  };

    if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF7B47]"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>GOmax - Travel & Tourism</title>
        <meta
          name="description"
          content="Plateforme B2B de réservation d'hôtels en Tunisie"
        />
      </Helmet>

      {isLoggedIn && !isLoading && (
        <Header
          userData={userData}
          onLogout={handleLogout}
          onMarginChange={handleMarginChange}
          onRechargeClick={handleRechargeClick}
          onTransactionHistoryClick={handleTransactionHistoryClick}
          onBookingsClick={handleBookingsClick}
          onHomeClick={handleGoHome}
          onPartnersManagementClick={handlePartnersManagement}
          onRechargeRequestsClick={handleRechargeRequests}
          onMargeB2BControlClick={handleMargeB2BControl}
        />
      )}

      <Routes>
        <Route
          path="/login"
          element={
            isLoggedIn ? (
              <Navigate to="/" replace />
            ) : (
              <SignInPage onLogin={handleLogin} />
            )
          }
        />

        <Route
          path="/admin/partners"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              {isAdmin ? (
                <PartnersManagementPage onBack={handleGoHome} />
              ) : (
                <Navigate to="/" replace />
              )}
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/recharge-requests"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              {isAdmin ? (
                <RechargeRequestsPage onBack={handleGoHome} />
              ) : (
                <Navigate to="/" replace />
              )}
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/marge-b2b"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              {isAdmin ? (
                <MargeB2BControlPage onBack={handleGoHome} />
              ) : (
                <Navigate to="/" replace />
              )}
            </ProtectedRoute>
          }
        />

        <Route
          path="/"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <HomePage onSearch={handleSearch} cities={staticData.cities} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/loading"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <LoadingPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/results"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <ResultsPage
                searchData={searchData}
                onSelectHotel={handleSelectHotel}
                hotels={searchResults}
                cities={staticData.cities}
                onSearch={handleSearch}
              />
            </ProtectedRoute>
          }
        />

        <Route
          path="/details"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <DetailsPage
                hotel={selectedHotel}
                onBooking={handleBooking}
                searchData={searchData}
                margeB2B={userData.margeB2B}
                currentMargin={userData.margin}
                onReSearch={handleDetailsSearch}
                isAdmin={isAdmin} 
              />
            </ProtectedRoute>
          }
        />

        <Route
          path="/devis"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <DevisPage
                hotel={selectedHotel}
                selectedPension={selectedPension}
                selectedRoomVariants={selectedRoomVariants}
                selectedAllocation={selectedAllocation}
                searchData={searchData}
                currentBalance={userData.balance}
                currentMargin={userData.margin}
                margeB2B={userData.margeB2B}
                onBalanceUpdate={handleBalanceUpdate}
                onNavigateToRecharge={handleRechargeClick}
              />
            </ProtectedRoute>
          }
        />

        <Route
          path="/recharge"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <RechargePage
                onBack={handleGoHome}
                currentBalance={userData.balance}
              />
            </ProtectedRoute>
          }
        />

        {/* ✅ NEW: Transaction History Route */}
        <Route
          path="/transactions"
          element={
            <ProtectedRoute isLoggedIn={isLoggedIn}>
              <TransactionHistoryPage
                onBack={handleGoHome}
                userData={userData}
              />
            </ProtectedRoute>
          }
        />

        <Route
          path="*"
          element={<Navigate to={isLoggedIn ? "/" : "/login"} replace />}
        />
      </Routes>

      {showBookingsModal && (
        <BookingsModal
          bookings={bookings}
          isLoading={isLoadingBookings}
          onClose={() => setShowBookingsModal(false)}
          isAdmin={isAdmin}
        />
      )}
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}